import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hover from './Hover';
import logoNavy from '@/assets/logos/new-site/logo.svg';

export interface NavLink {
  label: string;
  href: string;
}

export interface HeaderCta {
  label: string;
  /** Internal route (e.g. /start-free). */
  to?: string;
  /** Hash anchor / mailto / external. */
  href?: string;
  bg: string;
  hoverBg: string;
}

interface SiteHeaderProps {
  navLinks: NavLink[];
  cta: HeaderCta;
  /** Nav link color (Hub uses #43434D, product pages #54545E). */
  linkColor?: string;
  bg?: string;
}

/** Sticky, blurred site header shared by the Hub and the three product pages. */
export default function SiteHeader({
  navLinks,
  cta,
  linkColor = '#54545E',
  bg = 'rgba(255,255,255,0.88)',
}: SiteHeaderProps) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  // Hide the sticky header when scrolling down, reveal it when scrolling up
  // (always shown near the top). A small delta avoids jitter.
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 80) setHidden(false);
      else if (y > last + 4) setHidden(true);
      else if (y < last - 4) setHidden(false);
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: bg,
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #ECEBE6',
        // Keep it shown while the mobile menu is open.
        transform: hidden && !open ? 'translateY(-100%)' : 'translateY(0)',
        transition: 'transform 0.25s ease',
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: '0 auto',
          padding: '16px 28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <Link to="/" style={{ display: 'flex', alignItems: 'center' }} onClick={close}>
          <img
            src={logoNavy}
            alt="F&B Insights"
            style={{ height: 42, width: 'auto', display: 'block' }}
          />
        </Link>

        {/* Desktop row / mobile dropdown (toggled by the hamburger). Layout and
            show/hide live in index.css (.site-nav / .site-hamburger). */}
        <nav className={`site-nav${open ? ' is-open' : ''}`}>
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={close}
              style={{ fontSize: 15, fontWeight: 500, color: linkColor }}
            >
              {link.label}
            </a>
          ))}
          <Hover
            to={cta.to}
            href={cta.href}
            onClick={close}
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: '#fff',
              background: cta.bg,
              padding: '10px 18px',
              borderRadius: 8,
              textAlign: 'center',
            }}
            hoverStyle={{ background: cta.hoverBg }}
          >
            {cta.label}
          </Hover>
        </nav>

        <button
          type="button"
          className="site-hamburger"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? '✕' : '☰'}
        </button>
      </div>
    </header>
  );
}
